// Site switch + port endpoints, keyed by bare site_slug:
//   GET|POST       /api/v1/sites/{s}/switches
//   GET|PUT|DELETE /api/v1/sites/{s}/switches/{switch_slug}
//   GET            /api/v1/sites/{s}/switches/{switch_slug}/ports
//   PUT|DELETE     /api/v1/sites/{s}/switches/{switch_slug}/ports/{number}
// Mirrors backend/src/orixen_hub/api/v1/site_network.py. Ports are upsert-by-
// number (no POST).

import { request, type ApiResponse } from "../client";
import type {
  PortIn,
  PortOut,
  SwitchIn,
  SwitchOut,
  SwitchUpdateIn,
} from "../types/siteSwitch";

const base = (s: string) => `/api/v1/sites/${encodeURIComponent(s)}/switches`;
const one = (s: string, switchSlug: string) =>
  `${base(s)}/${encodeURIComponent(switchSlug)}`;

export const siteSwitches = {
  list: (siteSlug: string): Promise<ApiResponse<SwitchOut[]>> =>
    request<SwitchOut[]>("GET", base(siteSlug)),

  get: (siteSlug: string, switchSlug: string): Promise<ApiResponse<SwitchOut>> =>
    request<SwitchOut>("GET", one(siteSlug, switchSlug)),

  create: (siteSlug: string, body: SwitchIn): Promise<ApiResponse<SwitchOut>> =>
    request<SwitchOut>("POST", base(siteSlug), body),

  update: (
    siteSlug: string,
    switchSlug: string,
    body: SwitchUpdateIn,
  ): Promise<ApiResponse<SwitchOut>> =>
    request<SwitchOut>("PUT", one(siteSlug, switchSlug), body),

  remove: (siteSlug: string, switchSlug: string): Promise<ApiResponse<void>> =>
    request<void>("DELETE", one(siteSlug, switchSlug)),

  listPorts: (
    siteSlug: string,
    switchSlug: string,
  ): Promise<ApiResponse<PortOut[]>> =>
    request<PortOut[]>("GET", `${one(siteSlug, switchSlug)}/ports`),

  putPort: (
    siteSlug: string,
    switchSlug: string,
    portNumber: number,
    body: PortIn,
  ): Promise<ApiResponse<PortOut>> =>
    request<PortOut>("PUT", `${one(siteSlug, switchSlug)}/ports/${portNumber}`, body),

  deletePort: (
    siteSlug: string,
    switchSlug: string,
    portNumber: number,
  ): Promise<ApiResponse<void>> =>
    request<void>("DELETE", `${one(siteSlug, switchSlug)}/ports/${portNumber}`),
};
